"use client"

import { memo, useRef, useEffect } from "react"
import { Loader2, Check } from "lucide-react"
import type { MainToolCall } from "@/lib/agent-demo-data"
import { cn } from "@/lib/utils"

/* ── Main tool call bubble ── */
export const MainToolBubble = memo(function MainToolBubble({ tool }: { tool: MainToolCall & { status: "running" | "done" } }) {
  const done = tool.status === "done"
  // One-shot mount animation: ref ensures className is applied only on first render
  const mountedRef = useRef(false)
  const isNew = !mountedRef.current
  useEffect(() => { mountedRef.current = true }, [])

  return (
    <div
      className={cn(
        "flex items-center gap-2.5",
        isNew && "animate-in fade-in slide-in-from-left-2 duration-200"
      )}
    >
      {/* Card — compact, title + paramLabel inline */}
      <div className="flex items-center gap-2 rounded-2xl rounded-tl-sm border border-border/40 bg-secondary/50 backdrop-blur-sm px-3.5 py-2 shadow-sm min-w-0">
        {done ? (
          <Check className="h-3 w-3 shrink-0 text-primary animate-in zoom-in duration-200" />
        ) : (
          <Loader2 className="h-3 w-3 shrink-0 animate-spin text-primary" />
        )}
        <span className="text-xs font-medium text-foreground/90 whitespace-nowrap">{tool.label}</span>
        {tool.paramLabel && (
          <span className="text-micro text-muted-foreground/50 whitespace-nowrap">{tool.paramLabel}</span>
        )}
      </div>

      {/* Result — outside the card, right side, distinct typography */}
      {done && tool.result && (
        <span className="text-caption font-medium text-muted-foreground whitespace-nowrap animate-in fade-in slide-in-from-left-1 duration-300">
          {tool.result}
        </span>
      )}
    </div>
  )
})
